import { parsePRJ } from '~/lib/parsers/prj.js'
import { parsePGM } from '~/lib/parsers/pgm.js'
import { parsePAC } from '~/lib/parsers/pac.js'
import { parseALL } from '~/lib/parsers/all.js'
import { checkAllFormat, checkPgmFormat } from '~/lib/parsers/format-check.js'

function extOf(filename: string) {
  const m = filename.match(/\.([^.]+)$/)
  return m ? m[1]!.toUpperCase() : ''
}

const SAMPLE_DECODE_CONCURRENCY = 4

async function runLimited<T>(items: T[], limit: number, worker: (item: T) => Promise<void>) {
  let index = 0
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (index < items.length) {
      const item = items[index++]!
      await worker(item)
    }
  })
  await Promise.all(workers)
}

export const useProjectStore = defineStore('project', () => {
  const projectName = ref<string | null>(null)
  const status = ref('Load a project or library folder to begin.')
  const loading = ref(false)
  const loadError = ref<string | null>(null)
  const ignoredNestedFileCount = ref(0)
  // AudioEngine owns decoded buffers and WAV sampler metadata in plain Maps,
  // which keeps the engine framework-agnostic but gives Vue nothing reactive
  // to track. Bump this after reset/decode so panels reading engine maps
  // recompute instead of sticking on the pre-decode state.
  const sampleDataRevision = ref(0)

  // Per-file load progress for the loading modal — real deep reactivity
  // (not shallowRef) since individual entries get their `status` mutated
  // in place as each file resolves, which is exactly what the modal needs
  // to show live progress on a slow USB transfer.
  const loadingFiles = ref<{ name: string; size: number; status: 'pending' | 'loading' | 'done' | 'error' }[]>([])
  function markFile(name: string, status: 'loading' | 'done' | 'error') {
    const entry = loadingFiles.value.find((f) => f.name === name)
    if (entry) entry.status = status
  }

  // Parsed data is plain, large, and never needs per-field reactivity —
  // shallowRef avoids Vue deep-proxying big arrays (padToNote/noteToPad,
  // 64-track tables, hundreds of note events) for no benefit.
  const programs = shallowRef<{ name: string; pgm: any }[]>([])
  const programsByName = shallowRef<Map<string, any>>(new Map())
  const all = shallowRef<{ version: string; sequences: any[]; songs?: any[] } | null>(null)

  const currentProgramName = ref<string | null>(null)
  // 0-3 (banks A-D), 16 pads each — see usePadLayout.
  const activeBank = ref(0)
  const selectedPadIndex = ref<number | null>(null)

  // Version-awareness + sanity-check results (see format-check.js) — one
  // entry per .ALL/.PGM file loaded this project. A version-string
  // mismatch alone never sets `looksOk: false` (informational only, see
  // format-check.js's own comment on why); only implausible parsed values
  // (bad bar count/BPM, a size mismatch) do. Surfaced in the status line
  // and the Debug panel/diagnostic report, not a blocking error — the
  // project still loads and plays either way.
  const formatChecks = shallowRef<{ file: string; check: ReturnType<typeof checkAllFormat> }[]>([])

  let loadToken = 0

  function clearParsedData() {
    programs.value = []
    programsByName.value = new Map()
    all.value = null
    currentProgramName.value = null
    activeBank.value = 0
    selectedPadIndex.value = null
    formatChecks.value = []
  }

  function resetRuntime() {
    usePlaybackStore().stop()
    const engine = useAudioEngine()
    engine.reset()
    sampleDataRevision.value++
    useOverridesStore().resetAll()
    return engine
  }

  function isCurrentLoad(token: number) {
    return token === loadToken
  }

  function loadFailureMessage(name: string, err: unknown) {
    const message = err instanceof Error ? err.message : String(err ?? 'Unknown load error')
    return `Could not load "${name}". ${message}`
  }

  function clearProject(message = 'Load a project or library folder to begin.') {
    loadToken += 1
    resetRuntime()
    projectName.value = null
    status.value = message
    loading.value = false
    loadError.value = null
    ignoredNestedFileCount.value = 0
    loadingFiles.value = []
    clearParsedData()
  }

  function registerProgramAlias(programsByName: Map<string, any>, name: string, pgm: any) {
    if (!name || programsByName.has(name)) return
    programsByName.set(name, pgm)
  }

  function registerProgram(programsByName: Map<string, any>, name: string, pgm: any, aliases: string[] = []) {
    registerProgramAlias(programsByName, name, pgm)
    // A track record's own program-name field is only 15 bytes (see
    // FORMAT.md), so a program whose real filename is 16+ characters gets
    // silently truncated wherever a track references it.
    registerProgramAlias(programsByName, name.slice(0, 15), pgm)
    for (const alias of aliases) {
      registerProgramAlias(programsByName, alias, pgm)
      registerProgramAlias(programsByName, alias.slice(0, 15), pgm)
    }
  }

  async function loadProject(files: File[], name = 'Project', options: { ignoredNestedFileCount?: number } = {}) {
    const token = ++loadToken
    const isCurrent = () => isCurrentLoad(token)
    // Stop whatever's playing and drop decoded samples from the previous
    // project *before* touching anything else — otherwise a playing
    // sequence keeps looping (its setTimeout-driven scheduler has no idea
    // the project changed) and stale buffers linger in memory.
    const engine = resetRuntime()
    // Live level/pan/tuning/mute edits are keyed by program & sequence
    // *name*, which collide constantly across projects ("Program01" etc.) —
    // drop them too, or an edit from the last project could silently apply
    // to this one.
    clearParsedData()

    loading.value = true
    loadError.value = null
    projectName.value = name
    ignoredNestedFileCount.value = options.ignoredNestedFileCount ?? 0
    status.value = `Loading ${files.length} files…`

    try {
      const prjFile = files.find((f) => extOf(f.name) === 'PRJ')
      const pgmFiles = files.filter((f) => extOf(f.name) === 'PGM').sort((a, b) => a.name.localeCompare(b.name))
      const pacFiles = files.filter((f) => extOf(f.name) === 'PAC').sort((a, b) => a.name.localeCompare(b.name))
      const allFile = files.find((f) => extOf(f.name) === 'ALL')
      const wavFiles = files.filter((f) => extOf(f.name) === 'WAV')

      if (!allFile) {
        const message = 'No .ALL file found in this folder - pick a full MPC1000/JJOS project folder.'
        loadError.value = message
        status.value = message
        loading.value = false
        return false
      }

      // Listed in roughly the order they'll actually load — PRJ/PGMs/PACs
      // first (small metadata plus packed samples), then the .ALL, then
      // decoded sample buffers — so the loading modal reads top-to-bottom
      // the same way progress actually happens.
      loadingFiles.value = [
        ...(prjFile ? [prjFile] : []),
        ...pgmFiles,
        ...pacFiles,
        allFile,
        ...wavFiles,
      ].map((f) => ({ name: f.name, size: f.size, status: 'pending' as const }))

      if (prjFile) {
        markFile(prjFile.name, 'loading')
        const prjText = await prjFile.text()
        if (!isCurrent()) return false
        parsePRJ(prjText)
        markFile(prjFile.name, 'done')
      }

      const newFormatChecks: { file: string; check: ReturnType<typeof checkAllFormat> }[] = []

      const newPrograms: { name: string; pgm: any }[] = []
      const newProgramsByName = new Map<string, any>()
      const packedSampleJobs: { loadingName: string; sampleName: string; size: number; buffer: ArrayBuffer }[] = []
      for (const f of pgmFiles) {
        markFile(f.name, 'loading')
        const buf = await f.arrayBuffer()
        if (!isCurrent()) return false
        const programName = f.name.replace(/\.PGM$/i, '')
        const pgm = parsePGM(buf, programName)
        newFormatChecks.push({ file: f.name, check: checkPgmFormat(pgm, buf.byteLength) })
        newPrograms.push({ name: programName, pgm })
        registerProgram(newProgramsByName, programName, pgm)
        markFile(f.name, 'done')
      }
      for (const f of pacFiles) {
        markFile(f.name, 'loading')
        const buf = await f.arrayBuffer()
        if (!isCurrent()) return false
        const fileProgramName = f.name.replace(/\.PAC$/i, '')
        const pac = parsePAC(buf, fileProgramName)
        const programName = pac.program.name || fileProgramName
        newPrograms.push({ name: programName, pgm: pac.program })
        registerProgram(newProgramsByName, programName, pac.program, [fileProgramName])
        for (const sample of pac.samples) {
          const sampleNames = [sample.name, ...(sample.aliases ?? [])]
          for (const sampleName of sampleNames) {
            packedSampleJobs.push({
              loadingName: `${f.name} / ${sampleName}.WAV`,
              sampleName,
              size: sample.wavBuffer.byteLength,
              buffer: sample.wavBuffer,
            })
          }
        }
        markFile(f.name, 'done')
      }
      if (!isCurrent()) return false
      programs.value = newPrograms
      programsByName.value = newProgramsByName
      currentProgramName.value = newPrograms[0]?.name ?? null

      if (packedSampleJobs.length) {
        loadingFiles.value = [
          ...loadingFiles.value,
          ...packedSampleJobs.map((job) => ({
            name: job.loadingName,
            size: job.size,
            status: 'pending' as const,
          })),
        ]
      }

      const sampleJobs = [
        ...wavFiles.map((f) => ({
          loadingName: f.name,
          sampleName: f.name.replace(/\.WAV$/i, ''),
          readBuffer: () => f.arrayBuffer(),
        })),
        ...packedSampleJobs.map((job) => ({
          loadingName: job.loadingName,
          sampleName: job.sampleName,
          readBuffer: async () => job.buffer,
        })),
      ]

      status.value = `Decoding ${sampleJobs.length} samples…`
      await runLimited(
        sampleJobs,
        SAMPLE_DECODE_CONCURRENCY,
        async (job) => {
          markFile(job.loadingName, 'loading')
          try {
            const buf = await job.readBuffer()
            if (!isCurrent()) return
            await engine.loadSample(job.sampleName, buf, { shouldKeep: isCurrent })
            if (!isCurrent()) return
            markFile(job.loadingName, 'done')
          } catch (err) {
            if (!isCurrent()) return
            // A read failure here (as opposed to a decode failure, which
            // loadSample already catches internally into decodeWarnings) is
            // exactly the flaky-USB case this modal exists for — don't let
            // one bad file abort the whole Promise.all batch.
            console.warn(`Failed to read ${job.loadingName}:`, err)
            markFile(job.loadingName, 'error')
          }
        }
      )
      if (!isCurrent()) return false
      sampleDataRevision.value++

      markFile(allFile.name, 'loading')
      const allBuf = await allFile.arrayBuffer()
      if (!isCurrent()) return false
      const parsedAll = parseALL(allBuf)
      if (!isCurrent()) return false
      all.value = parsedAll
      activeBank.value = 0
      newFormatChecks.push({ file: allFile.name, check: checkAllFormat(parsedAll, allBuf.byteLength) })
      formatChecks.value = newFormatChecks
      markFile(allFile.name, 'done')

      const structuralIssues = newFormatChecks.filter((fc) => !fc.check.looksOk)
      status.value =
        `Loaded: ${newPrograms.length} program(s), ${sampleJobs.length} sample(s), ` +
        `${parsedAll.sequences.length} sequence(s) with recorded data` +
        (parsedAll.songs?.length ? `, ${parsedAll.songs.length} song(s)` : '') +
        `.` +
        (ignoredNestedFileCount.value > 0
          ? ` Ignored ${ignoredNestedFileCount.value} nested file(s).`
          : '') +
        (structuralIssues.length > 0
          ? ` ⚠ ${structuralIssues.length} file(s) don't look right - see 🐛 Debug for details.`
          : '')
      loading.value = false

      if (parsedAll.sequences.length > 0) {
        usePlaybackStore().focus(parsedAll.sequences[0])
      }
      return true
    } catch (err) {
      if (!isCurrent()) return false
      const activeEntry = loadingFiles.value.find((f) => f.status === 'loading') ?? loadingFiles.value.find((f) => f.status === 'pending')
      if (activeEntry) activeEntry.status = 'error'
      resetRuntime()
      clearParsedData()
      const message = loadFailureMessage(name, err)
      loadError.value = message
      status.value = message
      loading.value = false
      return false
    }
  }

  function selectProgram(name: string) {
    currentProgramName.value = name
    activeBank.value = 0
    selectedPadIndex.value = null
  }

  function selectBank(bank: number) {
    activeBank.value = bank
  }

  function selectPadIndex(index: number | null, options: { revealBank?: boolean } = {}) {
    if (index == null || !Number.isFinite(index)) {
      selectedPadIndex.value = null
      return
    }
    const next = Math.max(0, Math.min(63, Math.trunc(index)))
    selectedPadIndex.value = next
    if (options.revealBank) activeBank.value = Math.floor(next / 16)
  }

  return {
    projectName,
    status,
    loading,
    loadError,
    loadingFiles,
    sampleDataRevision,
    programs,
    programsByName,
    all,
    currentProgramName,
    activeBank,
    selectedPadIndex,
    formatChecks,
    ignoredNestedFileCount,
    clearProject,
    loadProject,
    selectProgram,
    selectBank,
    selectPadIndex,
  }
})
